<template>
  <div class="admin-device-management">
    <div class="page-header">
      <div class="header-left">
        <h1>设备管理</h1>
        <p>管理系统中的所有设备，包括未绑定设备</p>
      </div>
      <div class="header-actions">
        <el-button type="success" @click="showAddDeviceDialog">
          <i class="el-icon-plus"></i> 添加设备
        </el-button>
        <el-button type="primary" @click="showBatchAddDialog">
          <i class="el-icon-upload"></i> 批量导入
        </el-button>
        <el-button @click="refreshData" :loading="loading">
          <i class="el-icon-refresh"></i> 刷新
        </el-button>
      </div>
    </div>

    <!-- 设备统计 -->
    <div class="device-stats">
      <el-row :gutter="20">
        <el-col :span="6">
          <div class="stat-card total">
            <div class="stat-icon">
              <i class="el-icon-mobile"></i>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ statistics.totalDevices || 0 }}</div>
              <div class="stat-label">总设备数</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card bound">
            <div class="stat-icon">
              <i class="el-icon-link"></i>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ statistics.boundDevices || 0 }}</div>
              <div class="stat-label">已绑定</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card unbound">
            <div class="stat-icon">
              <i class="el-icon-warning"></i>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ statistics.unboundDevices || 0 }}</div>
              <div class="stat-label">未绑定</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card online">
            <div class="stat-icon">
              <i class="el-icon-success"></i>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ statistics.onlineDevices || 0 }}</div>
              <div class="stat-label">在线设备</div>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 筛选和搜索 -->
    <div class="filter-section">
      <div class="filter-left">
        <el-radio-group v-model="filterType" @change="handleFilterChange">
          <el-radio-button label="all">全部设备</el-radio-button>
          <el-radio-button label="unbound">未绑定</el-radio-button>
          <el-radio-button label="bound">已绑定</el-radio-button>
          <el-radio-button label="online">在线</el-radio-button>
          <el-radio-button label="offline">离线</el-radio-button>
        </el-radio-group>
      </div>
      <div class="filter-right">
        <el-input
          v-model="searchQuery"
          placeholder="搜索设备编码、名称..."
          prefix-icon="el-icon-search"
          clearable
          @input="handleSearch"
          style="width: 300px; margin-right: 15px;"
        />
        <el-select v-model="selectedDeviceType" placeholder="设备类型" clearable @change="handleFilterChange">
          <el-option 
            v-for="type in deviceTypes" 
            :key="type" 
            :label="type" 
            :value="type"
          />
        </el-select>
      </div>
    </div>

    <!-- 设备表格 -->
    <div class="device-table-section">
      <el-table
        :data="filteredDevices"
        v-loading="loading"
        stripe
        style="width: 100%"
        @selection-change="handleSelectionChange"
        @sort-change="handleSortChange"
      >
        <el-table-column type="selection" width="55" />
        
        <el-table-column prop="deviceName" label="设备名称" min-width="140" header-align="center" align="center" show-overflow-tooltip />
        <el-table-column prop="deviceCode" label="设备编码" min-width="140" header-align="center" align="center" show-overflow-tooltip sortable="custom" />
        <el-table-column prop="deviceTypeName" label="设备类型" min-width="120" header-align="center" align="center">
          <template #default="scope">
            {{ scope.row.deviceTypeName || scope.row.deviceType || '-' }}
          </template>
        </el-table-column>

        <el-table-column label="绑定状态" width="100">
          <template #default="scope">
            <el-tag :type="isDeviceBound(scope.row) ? 'success' : 'warning'" size="small">
              {{ isDeviceBound(scope.row) ? '已绑定' : '未绑定' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="设备状态" width="100">
          <template #default="scope">
            <el-tag :type="getStatusType(scope.row.status)" size="small">
              {{ getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="型号" width="120">
          <template #default="scope">
            {{ scope.row.productModel || '-' }}
          </template>
        </el-table-column>

        <el-table-column label="所属家庭" width="150">
          <template #default="scope">
            <span v-if="scope.row.familyName">{{ scope.row.familyName }}</span>
            <span v-else class="text-muted">-</span>
          </template>
        </el-table-column>

        <el-table-column prop="createdAt" label="创建时间" width="160" header-align="center" align="center" sortable="custom">
          <template #default="scope">
            {{ formatDate(scope.row.createdAt) }}
          </template>
        </el-table-column>

        <el-table-column label="二维码" width="160" align="center">
          <template #default="scope">
            <el-button 
              link 
              @click="showQRCode(scope.row)" 
              class="qr-btn"
              title="查看二维码"
            >
              📱 查看二维码
            </el-button>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="200" fixed="right">
          <template #default="scope">
            <el-button link @click="viewDevice(scope.row)">查看</el-button>
            <el-button link @click="editDevice(scope.row)">编辑</el-button>
            <el-button 
              link 
              @click="deleteDevice(scope.row)"
              :disabled="isDeviceBound(scope.row)"
              :class="{ 'delete-btn': !isDeviceBound(scope.row), 'delete-btn-disabled': isDeviceBound(scope.row) }"
              :title="`设备绑定状态: ${isDeviceBound(scope.row) ? '已绑定' : '未绑定'}, familyId: ${scope.row.familyId}`"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 批量操作 -->
      <div class="batch-actions" v-if="selectedDevices.length > 0">
        <span>已选择 {{ selectedDevices.length }} 个设备</span>
        <el-button type="danger" size="small" @click="batchDeleteDevices">
          批量删除
        </el-button>
      </div>

      <!-- 分页 -->
      <div class="pagination-wrapper">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="totalDevices"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <!-- 添加设备对话框 -->
    <el-dialog
      :title="deviceForm.id ? '编辑设备' : '添加设备'"
      v-model="addDeviceDialogVisible"
      width="600px"
      :before-close="handleCloseAddDevice"
    >
      <el-form :model="deviceForm" :rules="deviceRules" ref="deviceForm" label-width="100px">
        <el-form-item label="设备编码" prop="deviceCode">
          <el-input v-model="deviceForm.deviceCode" placeholder="请输入设备编码" />
        </el-form-item>
        <el-form-item label="设备名称" prop="deviceName">
          <el-input v-model="deviceForm.deviceName" placeholder="请输入设备名称" />
        </el-form-item>
        <el-form-item label="产品" prop="productId">
          <el-select 
            v-model="deviceForm.productId" 
            placeholder="请选择产品" 
            style="width: 100%"
            @change="onProductChange"
          >
            <el-option
              v-for="product in products"
              :key="product.id"
              :label="product.name"
              :value="product.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="设备型号">
          <el-input v-model="deviceForm.deviceModel" placeholder="请输入设备型号" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input 
            v-model="deviceForm.description" 
            type="textarea" 
            :rows="3"
            placeholder="请输入设备描述"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="fillTestData" type="info" size="small">填充测试数据</el-button>
          <el-button @click="addDeviceDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleAddDevice" :loading="addDeviceLoading">
            确定
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 批量添加对话框 -->
    <el-dialog
      title="批量导入设备"
      v-model="batchAddDialogVisible"
      width="800px"
    >
      <div class="batch-add-content">
        <el-alert
          title="批量导入说明"
          type="info"
          :closable="false"
          style="margin-bottom: 20px;"
        >
          <p>1. 先选择要导入的产品类型</p>
          <p>2. 上传包含IMEI号的CSV文件（仅包含一列IMEI数据）</p>
          <p>3. 系统将自动为每个IMEI创建设备记录</p>
        </el-alert>

        <el-form :model="batchForm" ref="batchForm" label-width="120px">
          <el-form-item label="选择产品" prop="productId" required>
            <el-select
              v-model="batchForm.productId"
              placeholder="请选择产品"
              style="width: 100%"
              @change="onBatchProductChange"
            >
              <el-option
                v-for="product in products"
                :key="product.id"
                :label="product.name"
                :value="product.id"
              />
            </el-select>
          </el-form-item>

          <el-form-item label="产品型号" v-if="batchForm.productModel">
            <el-input v-model="batchForm.productModel" disabled />
          </el-form-item>

          <el-form-item label="设备类型" v-if="batchForm.deviceType">
            <el-input v-model="batchForm.deviceType" disabled />
          </el-form-item>

          <el-form-item label="IMEI文件" prop="csvFile" required>
            <el-upload
              ref="csvUpload"
              :auto-upload="false"
              :show-file-list="true"
              :on-change="handleFileChange"
              :before-remove="handleFileRemove"
              accept=".csv"
              drag
              action="#"
            >
              <i class="el-icon-upload"></i>
              <div class="el-upload__text">
                将CSV文件拖到此处，或<em>点击上传</em>
              </div>
              <div class="el-upload__tip">
                只能上传CSV文件，文件应仅包含一列IMEI数据
              </div>
            </el-upload>
          </el-form-item>

          <el-form-item label="预览数据" v-if="batchForm.previewData.length > 0">
            <div class="preview-container">
              <p>将导入 {{ batchForm.previewData.length }} 个设备：</p>
              <div class="preview-list">
                <div v-for="(imei, index) in batchForm.previewData.slice(0, 5)" :key="index" class="preview-item">
                  {{ index + 1 }}. IMEI: {{ imei }}
                </div>
                <div v-if="batchForm.previewData.length > 5" class="preview-more">
                  ... 还有 {{ batchForm.previewData.length - 5 }} 个
                </div>
              </div>
            </div>
          </el-form-item>
        </el-form>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="batchAddDialogVisible = false">取消</el-button>
          <el-button
            type="primary"
            @click="handleBatchImport"
            :loading="batchAddLoading"
            :disabled="!batchForm.productId || batchForm.previewData.length === 0"
          >
            开始导入 ({{ batchForm.previewData.length }} 个设备)
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 二维码对话框 -->
    <el-dialog
      title="设备二维码"
      v-model="qrDialogVisible"
      width="450px"
      :show-close="true"
    >
      <div class="qr-dialog-content">
        <div class="device-info">
          <p><strong>设备名称：</strong>{{ currentDevice?.deviceName || '未命名设备' }}</p>
          <p><strong>设备编码：</strong>{{ currentDevice?.deviceCode }}</p>
          <p><strong>设备型号：</strong>{{ currentDevice?.productModel || '-' }}</p>
        </div>
        <div class="qr-code-container">
          <canvas ref="qrCodeCanvas" width="200" height="200"></canvas>
          <div class="qr-code-text">{{ currentDevice?.deviceCode }}</div>
        </div>
        <div class="qr-actions">
          <el-button type="primary" @click="downloadQRCode">下载二维码</el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import QRCode from 'qrcode'

export default {
  name: 'AdminDeviceManagement',
  data() {
    return {
      loading: false,
      devices: [],
      statistics: {},
      products: [],
      
      // 筛选和搜索
      filterType: 'all',
      searchQuery: '',
      selectedDeviceType: '',
      deviceTypes: [],
      
      // 表格选择
      selectedDevices: [],
      
      // 分页
      currentPage: 1,
      pageSize: 10,
      totalDevices: 0,
      
      // 排序
      sortState: { prop: '', order: '' },
      
      // 添加设备
      addDeviceDialogVisible: false,
      addDeviceLoading: false,
      deviceForm: {
        id: null,
        deviceCode: '',
        deviceName: '',
        productId: '',
        deviceModel: '',
        description: ''
      },
      deviceRules: {
        deviceCode: [
          { required: true, message: '请输入设备编码', trigger: 'blur' }
        ],
        deviceName: [
          { required: true, message: '请输入设备名称', trigger: 'blur' }
        ],
        productId: [
          { required: true, message: '请选择产品', trigger: 'change' }
        ]
      },
      
      // 批量添加
      batchAddDialogVisible: false,
      batchAddLoading: false,
      batchForm: {
        productId: '',
        productModel: '',
        deviceType: '',
        csvFile: null,
        previewData: []
      },
      
      // 二维码
      qrDialogVisible: false,
      currentDevice: null
    }
  },
  computed: {
    filteredDevices() {
      let result = [...this.devices]
      
      // 按类型筛选
      if (this.filterType === 'unbound') {
        result = result.filter(device => !this.isDeviceBound(device))
      } else if (this.filterType === 'bound') {
        result = result.filter(device => this.isDeviceBound(device))
      } else if (this.filterType === 'online') {
        result = result.filter(device => {
          const deviceStatus = Number(device.status)
          return deviceStatus === 1
        })
      } else if (this.filterType === 'offline') {
        result = result.filter(device => {
          const deviceStatus = Number(device.status)
          return deviceStatus === 0
        })
      }
      
      // 按设备类型筛选
      if (this.selectedDeviceType) {
        result = result.filter(device => device.deviceTypeName === this.selectedDeviceType)
      }
      
      // 搜索筛选
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        result = result.filter(device => 
          (device.deviceCode && device.deviceCode.toLowerCase().includes(query)) ||
          (device.deviceName && device.deviceName.toLowerCase().includes(query)) ||
          (device.deviceModel && device.deviceModel.toLowerCase().includes(query))
        )
      }
      
      // 排序
      const { prop, order } = this.sortState
      if (prop && order) {
        const isAsc = order === 'ascending'
        const compare = (a, b) => {
          let va = a[prop]
          let vb = b[prop]
          if (va == null && vb == null) return 0
          if (va == null) return 1
          if (vb == null) return -1
          if (prop === 'createdAt') {
            const ta = new Date(va).getTime() || 0
            const tb = new Date(vb).getTime() || 0
            return isAsc ? ta - tb : tb - ta
          }
          if (typeof va === 'number' && typeof vb === 'number') {
            return isAsc ? va - vb : vb - va
          }
          va = String(va)
          vb = String(vb)
          return isAsc ? va.localeCompare(vb) : vb.localeCompare(va)
        }
        result.sort(compare)
      }
      
      // 更新总数
      this.totalDevices = result.length
      
      // 分页处理
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      
      return result.slice(start, end)
    }
  },
  created() {
    // 检查URL参数
    const filter = this.$route.query.filter
    if (filter) {
      this.filterType = filter
    }
    
    this.loadData()
  },
  methods: {
    handleSortChange({ prop, order }) {
      this.sortState = { prop, order }
      // 切换排序时回到第一页
      this.currentPage = 1
    },
    async loadData() {
      try {
        this.loading = true
        await Promise.all([
          this.loadDevices(),
          this.loadStatistics(),
          this.loadProducts(),
          this.loadDeviceTypes()
        ])
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$message.error('加载数据失败')
      } finally {
        this.loading = false
      }
    },

    async loadDevices() {
      try {
        const response = await this.$http.get('/api/admin/devices/all')
        if (response.data.success) {
          this.devices = response.data.data
          console.log('加载的设备数据:', this.devices.map(d => ({ 
            id: d.id, 
            name: d.deviceName, 
            deviceCode: d.deviceCode,
            deviceModel: d.deviceModel,
            productModel: d.productModel,
            familyId: d.familyId, 
            bound: this.isDeviceBound(d) 
          })))
          
          // 检查第一个设备的完整数据
          if (this.devices.length > 0) {
            console.log('第一个设备的完整数据:', this.devices[0])
          }
          
          // 设备类型数据已单独加载，这里不再提取
        }
      } catch (error) {
        console.error('加载设备列表失败:', error)
        throw error
      }
    },

    async loadStatistics() {
      try {
        const response = await this.$http.get('/api/admin/devices/statistics')
        if (response.data.success) {
          this.statistics = response.data.data
        }
      } catch (error) {
        console.error('加载设备统计失败:', error)
        throw error
      }
    },

    async loadProducts() {
      try {
        const response = await this.$http.get('/api/products')
        if (response.data.success) {
          this.products = response.data.data
        }
      } catch (error) {
        console.error('加载产品列表失败:', error)
      }
    },

    async loadDeviceTypes() {
      try {
        const response = await this.$http.get('/api/admin/devices/device-types')
        if (response.data.success) {
          this.deviceTypes = response.data.data.map(type => type.name)
        }
      } catch (error) {
        console.error('加载设备类型失败:', error)
      }
    },

    onProductChange(productId) {
      if (productId) {
        const selectedProduct = this.products.find(p => p.id === productId)
        if (selectedProduct) {
          this.deviceForm.deviceModel = selectedProduct.model
        }
      } else {
        this.deviceForm.deviceModel = ''
      }
    },

    // 判断设备是否已绑定家庭
    isDeviceBound(device) {
      return device.familyId && 
             device.familyId !== null && 
             device.familyId !== undefined && 
             device.familyId.toString().trim() !== ''
    },

    // 显示二维码
    showQRCode(device) {
      console.log('显示二维码，设备数据:', device)
      this.currentDevice = device
      this.qrDialogVisible = true
      this.$nextTick(() => {
        console.log('生成二维码，设备编码:', device.deviceCode)
        this.generateQRCode(device.deviceCode)
      })
    },

    // 生成二维码
    async generateQRCode(text) {
      console.log('开始生成二维码，文本:', text)
      const canvas = this.$refs.qrCodeCanvas
      if (!canvas) {
        console.log('Canvas元素未找到')
        return
      }

      if (!text) {
        console.log('文本为空，使用默认值')
        text = '未知设备'
      }

      try {
        // 使用qrcode库生成二维码
        await QRCode.toCanvas(canvas, text, {
          width: 200,
          height: 200,
          errorCorrectionLevel: 'H',
          type: 'image/png',
          quality: 0.92,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        })
        console.log('二维码生成成功')
      } catch (error) {
        console.error('生成二维码失败:', error)
        // 如果生成失败，显示错误信息
        this.drawErrorMessage(canvas, '二维码生成失败')
      }
    },

    // 绘制错误信息
    drawErrorMessage(canvas, message) {
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, 200, 200)
      
      // 绘制背景
      ctx.fillStyle = '#f5f5f5'
      ctx.fillRect(0, 0, 200, 200)
      
      // 绘制边框
      ctx.strokeStyle = '#ddd'
      ctx.lineWidth = 2
      ctx.strokeRect(0, 0, 200, 200)
      
      // 绘制错误文本
      ctx.fillStyle = '#999'
      ctx.font = '14px Arial'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(message, 100, 100)
    },


    // 下载二维码
    downloadQRCode() {
      const canvas = this.$refs.qrCodeCanvas
      if (!canvas) return

      try {
        // 将canvas转换为图片
        const imageDataUrl = canvas.toDataURL('image/png')
        
        // 创建下载链接
        const link = document.createElement('a')
        link.download = `qr-code-${this.currentDevice?.deviceCode || 'device'}.png`
        link.href = imageDataUrl
        
        // 触发下载
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        this.$message.success('二维码下载成功')
      } catch (error) {
        console.error('下载二维码失败:', error)
        this.$message.error('下载二维码失败')
      }
    },

    refreshData() {
      this.loadData()
    },

    handleFilterChange() {
      // 筛选变化时重置到第一页
      this.currentPage = 1
    },

    handleSearch() {
      // 搜索变化时重置到第一页
      this.currentPage = 1
    },

    handleSizeChange(newSize) {
      this.pageSize = newSize
      this.currentPage = 1
    },

    handleCurrentChange(newPage) {
      this.currentPage = newPage
    },

    handleSelectionChange(selection) {
      this.selectedDevices = selection
    },

    getStatusType(status) {
      // 确保status是数字类型
      const deviceStatus = Number(status)
      console.log('AdminDeviceManagement - getStatusType - 原始值:', status, '类型:', typeof status, '转换后:', deviceStatus)
      if (deviceStatus === 1) return 'success'
      if (deviceStatus === 0) return 'danger'
      return 'info'
    },

    getStatusText(status) {
      // 确保status是数字类型
      const deviceStatus = Number(status)
      console.log('AdminDeviceManagement - getStatusText - 原始值:', status, '类型:', typeof status, '转换后:', deviceStatus)
      if (deviceStatus === 1) return '在线'
      if (deviceStatus === 0) return '离线'
      return '未知'
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN')
    },

    viewDevice(device) {
      // 查看设备详情
      this.$router.push(`/admin/devices/${device.id}`)
    },

    editDevice(device) {
      // 编辑设备
      this.deviceForm = {
        ...device,
        deviceModel: device.deviceModel || device.productModel || ''
      }
      this.addDeviceDialogVisible = true
    },

    async deleteDevice(device) {
      console.log('准备删除设备:', device)
      console.log('设备familyId:', device.familyId, '类型:', typeof device.familyId)
      console.log('设备是否绑定:', this.isDeviceBound(device))
      
      if (this.isDeviceBound(device)) {
        this.$message.warning('无法删除已绑定家庭的设备')
        return
      }

      try {
        await this.$confirm(`确定要删除设备"${device.deviceName || device.deviceCode}"吗？`, '确认删除', {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning'
        })

        const response = await this.$http.delete(`/api/admin/devices/${device.id}`)
        if (response.data.success) {
          this.$message.success('设备删除成功')
          this.loadData()
        } else {
          this.$message.error(response.data.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除设备失败:', error)
          this.$message.error('删除失败')
        }
      }
    },

    async batchDeleteDevices() {
      const unboundDevices = this.selectedDevices.filter(device => !this.isDeviceBound(device))
      
      if (unboundDevices.length === 0) {
        this.$message.warning('只能删除未绑定的设备')
        return
      }

      try {
        await this.$confirm(`确定要删除选中的 ${unboundDevices.length} 个设备吗？`, '批量删除', {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning'
        })

        // 这里需要后端提供批量删除接口
        for (const device of unboundDevices) {
          await this.$http.delete(`/api/admin/devices/${device.id}`)
        }

        this.$message.success('批量删除成功')
        this.selectedDevices = []
        this.loadData()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('批量删除失败:', error)
          this.$message.error('批量删除失败')
        }
      }
    },

    showAddDeviceDialog() {
      this.resetDeviceForm()
      this.addDeviceDialogVisible = true
    },

    showBatchAddDialog() {
      console.log('批量导入按钮被点击了')
      // 重置批量导入表单
      this.batchForm = {
        productId: '',
        productModel: '',
        deviceType: '',
        csvFile: null,
        previewData: []
      }
      console.log('设置对话框可见性为true')
      this.batchAddDialogVisible = true
      console.log('当前对话框状态:', this.batchAddDialogVisible)
    },

    // 产品选择变化时的处理
    onBatchProductChange(productId) {
      const selectedProduct = this.products.find(p => p.id === productId)
      if (selectedProduct) {
        this.batchForm.productModel = selectedProduct.model || ''
        this.batchForm.deviceType = selectedProduct.type || selectedProduct.deviceType || ''
      } else {
        this.batchForm.productModel = ''
        this.batchForm.deviceType = ''
      }
    },

    // 处理文件上传
    handleFileChange(file, fileList) {
      if (file.status === 'ready') {
        this.batchForm.csvFile = file.raw
        this.parseCSVFile(file.raw)
      }
    },

    // 处理文件移除
    handleFileRemove(file, fileList) {
      this.batchForm.csvFile = null
      this.batchForm.previewData = []
    },

    // 解析CSV文件
    parseCSVFile(file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const text = e.target.result
          const lines = text.split('\n')
          const imeiList = []

          for (let line of lines) {
            const trimmedLine = line.trim()
            if (trimmedLine && trimmedLine.length > 0) {
              // 简单验证IMEI格式（通常15位数字）
              if (/^\d{15}$/.test(trimmedLine)) {
                imeiList.push(trimmedLine)
              } else {
                console.warn('Invalid IMEI format:', trimmedLine)
              }
            }
          }

          this.batchForm.previewData = imeiList

          if (imeiList.length === 0) {
            this.$message.warning('CSV文件中没有找到有效的IMEI数据')
          } else {
            this.$message.success(`成功解析 ${imeiList.length} 个有效IMEI`)
          }
        } catch (error) {
          console.error('解析CSV文件失败:', error)
          this.$message.error('解析CSV文件失败，请检查文件格式')
        }
      }
      reader.readAsText(file)
    },

    handleCloseAddDevice() {
      this.addDeviceDialogVisible = false
      this.resetDeviceForm()
    },

    resetDeviceForm() {
      this.deviceForm = {
        id: null, // 清除id字段
        deviceCode: '',
        deviceName: '',
        productId: '',
        deviceModel: '',
        description: ''
      }
      this.$nextTick(() => {
        this.$refs.deviceForm?.clearValidate()
      })
    },

    fillTestData() {
      const timestamp = Date.now().toString().slice(-6)
      this.deviceForm = {
        deviceCode: `TEST_DEV_${timestamp}`,
        deviceName: `测试设备_${timestamp}`,
        productId: this.products.length > 0 ? this.products[0].id : '',
        deviceModel: 'TEST-MODEL-v1.0',
        description: '这是一个测试设备，用于验证添加设备功能'
      }
    },

    async handleAddDevice() {
      try {
        const valid = await this.$refs.deviceForm.validate()
        if (!valid) return

        this.addDeviceLoading = true

        // 判断是新增还是编辑
        const isEdit = !!this.deviceForm.id
        let response

        if (isEdit) {
          // 编辑设备 - 调用更新接口
          const updateData = {
            id: this.deviceForm.id,
            deviceCode: String(this.deviceForm.deviceCode || ''),
            deviceName: String(this.deviceForm.deviceName || ''),
            productId: String(this.deviceForm.productId || ''),
            deviceModel: String(this.deviceForm.deviceModel || ''),
            description: String(this.deviceForm.description || ''),
            deviceId: String(this.deviceForm.deviceId || '')
          }
          response = await this.$http.put(`/api/admin/devices/${String(this.deviceForm.deviceId)}`, updateData)
        } else {
          // 新增设备 - 调用添加接口
          response = await this.$http.post('/api/admin/devices/add', this.deviceForm)
        }

        if (response.data.success) {
          this.$message.success(isEdit ? '设备更新成功' : '设备添加成功')
          this.addDeviceDialogVisible = false
          this.resetDeviceForm()
          this.loadData()
        } else {
          this.$message.error(response.data.message || (isEdit ? '设备更新失败' : '设备添加失败'))
        }
      } catch (error) {
        console.error('添加设备失败:', error)
        this.$message.error(error.response?.data?.message || '添加设备失败')
      } finally {
        this.addDeviceLoading = false
      }
    },

    async handleBatchImport() {
      if (!this.batchForm.productId) {
        this.$message.warning('请选择产品')
        return
      }

      if (this.batchForm.previewData.length === 0) {
        this.$message.warning('请上传包含IMEI数据的CSV文件')
        return
      }

      try {
        this.batchAddLoading = true

        // 获取选中的产品信息
        const selectedProduct = this.products.find(p => p.id === this.batchForm.productId)

        // 为每个IMEI创建设备记录
        const devices = this.batchForm.previewData.map((imei, index) => ({
          deviceCode: imei, // 使用IMEI作为设备编码
          deviceName: `${selectedProduct.name}_${imei.slice(-4)}`, // 产品名称+IMEI后4位作为设备名称
          productId: this.batchForm.productId,
          deviceModel: this.batchForm.productModel,
          description: `批量导入的${selectedProduct.name}设备，IMEI: ${imei}`,
          deviceId: imei // 使用IMEI作为设备ID
        }))

        console.log('准备批量导入的设备数据:', devices)

        const response = await this.$http.post('/api/admin/devices/batch-add', { devices })

        if (response.data.success) {
          const { successCount, failCount } = response.data
          this.$message.success(`批量导入完成：成功 ${successCount} 个，失败 ${failCount} 个`)

          if (failCount > 0 && response.data.errors) {
            this.$message.warning(`部分设备导入失败：${response.data.errors}`)
          }

          this.batchAddDialogVisible = false
          this.resetBatchForm()
          this.loadData()
        } else {
          this.$message.error(response.data.message || '批量导入失败')
        }
      } catch (error) {
        console.error('批量导入失败:', error)
        this.$message.error(error.response?.data?.message || '批量导入失败')
      } finally {
        this.batchAddLoading = false
      }
    },

    // 重置批量导入表单
    resetBatchForm() {
      this.batchForm = {
        productId: '',
        productModel: '',
        deviceType: '',
        csvFile: null,
        previewData: []
      }
      // 清空文件上传组件
      if (this.$refs.csvUpload) {
        this.$refs.csvUpload.clearFiles()
      }
    }
  }
}
</script>

<style scoped>
.admin-device-management {
  padding: 20px;
}

/* 页面头部 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
}

.header-left h1 {
  margin: 0 0 8px 0;
  font-size: 28px;
  color: #333;
}

.header-left p {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.header-actions {
  display: flex;
  gap: 12px;
}

/* 统计卡片 */
.device-stats {
  margin-bottom: 30px;
}

.stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 25px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-5px);
}

.stat-card.total .stat-icon {
  background: linear-gradient(45deg, #667eea, #764ba2);
}

.stat-card.bound .stat-icon {
  background: linear-gradient(45deg, #52c41a, #73d13d);
}

.stat-card.unbound .stat-icon {
  background: linear-gradient(45deg, #fa8c16, #ffa940);
}

.stat-card.online .stat-icon {
  background: linear-gradient(45deg, #1890ff, #40a9ff);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
}

.stat-icon i {
  font-size: 24px;
  color: #fff;
}

.stat-number {
  font-size: 28px;
  font-weight: bold;
  color: #333;
  margin-bottom: 5px;
}

.stat-label {
  color: #666;
  font-size: 14px;
}

/* 筛选区域 */
.filter-section {
  background: #fff;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-right {
  display: flex;
  align-items: center;
  gap: 15px;
}

/* 设备表格 */
.device-table-section {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  min-height: 560px;
  padding-bottom: 20px;
}

.device-info .device-name {
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.device-info .device-code {
  font-size: 12px;
  color: #666;
  font-family: monospace;
  margin-bottom: 2px;
}

.device-info .device-type {
  font-size: 12px;
  color: #999;
}

.text-muted {
  color: #999;
}

.text-danger {
  color: #ff4d4f !important;
}

.text-danger:hover {
  color: #ff7875 !important;
}

.text-danger:not(.is-disabled) {
  color: #ff4d4f !important;
}

.text-danger:not(.is-disabled):hover {
  color: #ff7875 !important;
}

.el-button--text.text-danger:not(.is-disabled) {
  color: #ff4d4f !important;
}

.el-button--text.text-danger:not(.is-disabled):hover {
  color: #ff7875 !important;
  background-color: #fff2f0 !important;
}

/* 删除按钮样式 */
.el-button.delete-btn {
  color: #ff4d4f !important;
}

.el-button.delete-btn:hover {
  color: #ff7875 !important;
  background-color: #fff2f0 !important;
}

.el-button.delete-btn-disabled {
  color: #ccc !important;
  cursor: not-allowed !important;
}

.el-button.delete-btn-disabled:hover {
  color: #ccc !important;
  background-color: transparent !important;
}

/* 二维码按钮样式 */
.qr-btn {
  color: #409eff !important;
  font-size: 14px;
  padding: 8px 12px;
}

.qr-btn:hover {
  color: #66b1ff !important;
  background-color: #ecf5ff !important;
}

/* 二维码对话框样式 */
.qr-dialog-content {
  text-align: center;
}

.qr-dialog-content .device-info {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 6px;
}

.qr-dialog-content .device-info p {
  margin: 5px 0;
  color: #333;
}

.qr-code-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}

.qr-code-container canvas {
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 10px;
}

.qr-code-text {
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #666;
  text-align: center;
  margin-bottom: 10px;
}

.qr-actions {
  text-align: center;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

/* 批量操作 */
.batch-actions {
  padding: 15px 20px;
  border-top: 1px solid #eee;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* 分页样式 */
.pagination-wrapper {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

/* 批量添加内容 */
.batch-add-content .el-alert p {
  margin: 5px 0;
}

.batch-add-content .el-alert p:first-child {
  margin-top: 0;
}

.batch-add-content .el-alert p:last-child {
  margin-bottom: 0;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .filter-section {
    flex-direction: column;
    gap: 20px;
    align-items: stretch;
  }
  
  .filter-right {
    justify-content: space-between;
  }
}

@media (max-width: 768px) {
  .admin-device-management {
    padding: 15px;
  }
  
  .page-header {
    flex-direction: column;
    gap: 20px;
  }
  
  .header-actions {
    flex-wrap: wrap;
  }
  
  .device-stats .el-col {
    margin-bottom: 15px;
  }
  
  .filter-right {
    flex-direction: column;
    gap: 15px;
  }
}

/* 增加输入框宽度 */
.el-input__inner {
  width: 100% !important;
  min-width: 200px !important;
}

.el-input {
  width: 100% !important;
  min-width: 200px !important;
}

/* 增加选择框宽度 */
.el-select {
  width: 100% !important;
  min-width: 200px !important;
}

.el-select__wrapper {
  width: 100% !important;
  min-width: 200px !important;
}

/* 批量导入相关样式 */
.batch-add-content {
  max-height: 600px;
  overflow-y: auto;
}

.preview-container {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 12px;
  background-color: #f5f7fa;
}

.preview-list {
  max-height: 150px;
  overflow-y: auto;
  margin-top: 8px;
}

.preview-item {
  padding: 4px 0;
  border-bottom: 1px solid #e4e7ed;
  font-family: monospace;
  font-size: 13px;
}

.preview-item:last-child {
  border-bottom: none;
}

.preview-more {
  padding: 4px 0;
  color: #909399;
  font-style: italic;
}

.el-upload__tip {
  margin-top: 8px;
  color: #606266;
  font-size: 12px;
}
</style>